import { makeRequest } from './make-request';
import { fetchWithPromise, fetchWithCache } from './fetch-with-cache';
import { jwtToken } from '../context';
import { sendEmailToParent } from '../analytics/send-lead-to-parent';

export async function submitEmail(email, optIns, consolidatedPhone, consolidatedName, consolidatedZip) {
  let trustedFormCertUrl;
  if (jwtToken.trustedForm) {
    try {
      const certTokenInput = document.querySelector('input[name="xxTrustedFormCertUrl"]');
      trustedFormCertUrl = certTokenInput ? certTokenInput.value : '';
    } catch (ex) {
      console.error('error getting cert inputs');
      console.error(ex);
    }
  }
  sendEmailToParent(email);
  const payload = {
    email,
    optIns,
    trustedFormCertUrl,
    consolidatedPhone,
    consolidatedName,
    consolidatedZip
  };
  if (jwtToken.selectedSourceData?.sourceType) payload.sourceType = jwtToken.selectedSourceData.sourceType;
  if (jwtToken.selectedSourceData?.source) payload.source = jwtToken.selectedSourceData.source;
  if (jwtToken.selectedSourceData?.sourceId) payload.sourceId = jwtToken.selectedSourceData.sourceId;
  return await makeRequest('post', 'appointment/page/api/lead/email', payload);
}

export async function submitPhoneSearch(phone, optIn) {
  return await makeRequest('post', 'appointment/page/api/lead/phone-search', { phone, optIn });
}

export async function getHashEmail(name, phone, zip) {
  return await makeRequest('post', 'appointment/page/api/hash-email', { name, phone, zip });
}

export async function submitProduct(payload) {
  // Remembered so later question sections can auto-skip when tagged skipOnProductMatch
  jwtToken.selectedProduct = payload?.product || jwtToken.selectedProduct;
  return await makeRequest('post', 'appointment/page/api/lead/product', payload);
}

export async function submitHearAbout(payload) {
  return await makeRequest('post', 'appointment/page/api/lead/hear-about', payload);
}

export async function submitQuestionSections(payload) {
  return await makeRequest('post', 'appointment/page/api/lead/question-sections', payload);
}

/** Uploads a photo attached to a question answer; returns { url } (Wasabi/cdn.heavyset.tech) or { errorMessage } */
export async function uploadQuestionPhoto(file) {
  const formData = new FormData();
  formData.append('file', file);
  return await makeRequest('post', 'appointment/page/api/lead/question-photo', formData, undefined, true);
}

/**
 * Submits staff information.
 *
 * @param {Object} payload - The payload containing staff information.
 * @param {string} payload.staffId - The staff ID.
 * @returns {Promise<string>} - 'success'
 */
export async function submitStaff(payload) {
  return await makeRequest('post', 'appointment/page/api/lead/staff', payload);
}

export async function submitDetails(payload) {
  return await makeRequest('post', 'appointment/page/api/lead/details', payload);
}

export async function submitAppointmentNotes(payload) {
  return await makeRequest('post', 'appointment/page/api/appointment/notes', payload);
}

/**
 * Submits staff information.
 *
 * @param {Object} payload - The payload containing staff information.
 * @param {string} payload.homeYear - The staff ID.
 * @returns {Promise<string>} - 'success'
 */
export async function submitHomeYear(payload) {
  return await makeRequest('post', 'appointment/page/api/lead/home-year', payload);
}

export async function submitAddress(payload) {
  return await makeRequest('post', 'appointment/page/api/lead/address', payload);
}

export async function submitLead(userInfo) {
  let trustedFormPingUrl;
  if (jwtToken.selectedSourceData?.sourceType) {
    userInfo.sourceType = jwtToken.selectedSourceData.sourceType;
  }
  if (jwtToken.selectedSourceData?.source) {
    userInfo.source = jwtToken.selectedSourceData.source;
  }
  if (jwtToken.selectedSourceData?.sourceId) {
    userInfo.sourceId = jwtToken.selectedSourceData.sourceId;
  }
  if (jwtToken.trustedForm) {
    try {
      const certTokenInput = document.querySelector('input[name="xxTrustedFormCertUrl"]');
      const pingUrlInput = document.querySelector('input[name="xxTrustedFormPingUrl"]');

      const trustedFormCertUrl = certTokenInput ? certTokenInput.value : '';
      trustedFormPingUrl = pingUrlInput ? pingUrlInput.value : '';
      console.log('', { trustedFormCertUrl, trustedFormPingUrl });

      // Add the cert URL to userInfo
      userInfo.trustedFormCertUrl = trustedFormCertUrl;
      userInfo.trustedFormPingUrl = trustedFormPingUrl;
    } catch (ex) {
      console.error('error getting cert inputs');
      console.error(ex);
    }
  }
  return await makeRequest('post', 'appointment/page/api/lead', userInfo);
}

export async function submitPendingContact(payload) {
  return await makeRequest('post', 'appointment/page/api/lead/pending-contact', payload);
}

export async function submitContact(contactInfo) {
  return await makeRequest('post', 'appointment/page/api/contact', contactInfo);
}

export async function getTimeSlots() {
  return await fetchWithPromise('get-time-slots', 'appointment/page/api/slots');
}

// Polled after a submitLead() call resolves to leadStatus.crmPending (LeadPerfection orgs only).
// Returns the same shape submitLead() does — {status, skipConversionEvent, unsupported,
// needsHomeYear, invalidState, leafPhone} — still reporting crmPending while the background CRM
// lookup is in flight. Uses fetchWithPromise (not makeRequest) like every other GET in this file,
// since makeRequest's GET branch doesn't correctly attach the Authorization header.
export async function getLeadSubmitStatus() {
  return await fetchWithPromise('lead-submit-status', 'appointment/page/api/lead-status');
}

export async function getTimeSlotsByZip(zip) {
  return await fetchWithPromise(`get-time-slots-zip-${zip}`, 'appointment/page/api/slots-by-zip', { zip });
}

export async function submitAppointment(slotInfo) {
  if (jwtToken.selectedSourceData?.sourceType) {
    slotInfo.sourceType = jwtToken.selectedSourceData.sourceType;
  }
  if (jwtToken.selectedSourceData?.source) {
    slotInfo.source = jwtToken.selectedSourceData.source;
  }
  if (jwtToken.selectedSourceData?.sourceId) {
    slotInfo.sourceId = jwtToken.selectedSourceData.sourceId;
  }
  const result = await makeRequest('post', 'appointment/page/api/appointment', slotInfo);
  jwtToken.apptId = result?.data?.appointmentId;
  return result;
}

// Ends the session without booking a slot. Mirrors submitAppointment's body (same session/
// questionResults payload the server already expects) minus the slot/date/time fields, since no
// slot was chosen. Reuses the same JWT auth as POST /appointment via makeRequest.
export async function submitNoAppointment() {
  const payload = {};
  if (jwtToken.selectedSourceData?.sourceType) {
    payload.sourceType = jwtToken.selectedSourceData.sourceType;
  }
  if (jwtToken.selectedSourceData?.source) {
    payload.source = jwtToken.selectedSourceData.source;
  }
  if (jwtToken.selectedSourceData?.sourceId) {
    payload.sourceId = jwtToken.selectedSourceData.sourceId;
  }
  return await makeRequest('post', 'appointment/page/api/no-appointment', payload);
}

export async function getGoogleReviewInfo(placeId) {
  return await fetchWithCache(`get-review-${placeId}`, 'appointment/page/api/google-review', { placeId });
}

export async function getCountries(limit, search) {
  const key = `get-countries-${limit},${search}`;
  const { data, errorMessage } = await fetchWithPromise(key, 'appointment/page/api/countries', {
    limit,
    search
  });
  return { records: data, errorMessage };
}

export async function getStaff(limit, search) {
  const key = `get-staff-${limit},${search}`;
  const { data, errorMessage } = await fetchWithPromise(key, 'appointment/page/api/staff', {
    limit,
    search
  });
  const staff = data?.staff || [];
  return { records: staff, errorMessage };
}

export async function getSources(search) {
  const key = `get-sources-${search}`;
  const { data, errorMessage } = await fetchWithPromise(key, 'appointment/page/api/source-search', {
    search
  });
  const sources = data?.sources || [];
  return { records: sources, errorMessage };
}

export async function updateFormViewing() {
  return await makeRequest('post', 'appointment/page/api/lead/form-viewing', {});
}

export async function submitTruvolvFormStart() {
  return await makeRequest('post', 'appointment/page/api/truvolv-form-start', {});
}
